/**
 * Marketing attribution — pure, DOM-free, unit-tested.
 *
 * Why this exists (marketing/PRELAUNCH-FIXES.md, FIX-1): the web app used to
 * drop every `utm_*` parameter before a single analytics event was captured,
 * so no email, social or ad click could ever be attributed to a sign-up.
 *
 * Three small pieces:
 *
 * 1. `parseAttribution()` — reads the campaign parameters we care about out of
 *    a query string (plus referrer and landing path). Everything else in the
 *    query string is ignored. Never throws on malformed input.
 * 2. A first-touch store keyed `om_attribution` behind an injectable storage
 *    interface (localStorage-shaped). First touch wins for 12 months: once a
 *    record is stored it is never overwritten while it is valid, and nothing
 *    is stored unless the visit carries at least one `utm_*` or click id. A
 *    record older than `ATTRIBUTION_MAX_AGE_MS` is treated as absent (and
 *    removed when the storage can), which is the retention the cookie policy
 *    states for analytics storage (lib/strings/src/en/legal.ts, cookies →
 *    analytics). Every storage failure is swallowed — attribution must never
 *    break a page.
 * 3. An in-memory holder for the CURRENT page load's attribution, captured at
 *    boot BEFORE consent exists. A visitor who lands with `utm_*`, navigates
 *    inside the SPA (which drops the query string), and only then accepts
 *    cookies must still be attributed — so the boot snapshot is kept in memory
 *    and persisted only when analytics actually starts (consent + non-kiosk).
 *    Nothing is written to storage before that moment.
 *
 * The consumer is `web/index.ts`: it feeds the current visit in at
 * `initObservability()`, persists + registers at `startPostHog()`, and passes
 * the first touch as set-once person properties at `setUser()`.
 */

export const ATTRIBUTION_STORAGE_KEY = "om_attribution";

/**
 * Values longer than this are clamped; a URL param is not a text field. The
 * limit is in Unicode code points, not UTF-16 units, so a clamp can never split
 * a surrogate pair and produce a string that is invalid on the wire.
 */
export const ATTRIBUTION_MAX_LENGTH = 200;

/** How long a stored first touch stays valid: 12 months, as disclosed on /cookies. */
export const ATTRIBUTION_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export const CLICK_ID_KEYS = ["gclid", "fbclid"] as const;

/** Keys that count as a campaign signal — a visit with none of these is organic/direct. */
export const CAMPAIGN_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS] as const;

const CONTEXT_KEYS = ["referrer", "landing_path"] as const;

export type CampaignKey = (typeof CAMPAIGN_KEYS)[number];
type ContextKey = (typeof CONTEXT_KEYS)[number];
export type AttributionKey = CampaignKey | ContextKey;

/** Only the keys that were present survive; values are trimmed and clamped. */
export type Attribution = Partial<Record<AttributionKey, string>>;

export interface ParseAttributionInput {
  /** `window.location.search` — with or without the leading `?`. */
  search?: string | null;
  /** `document.referrer`. */
  referrer?: string | null;
  /** `window.location.pathname` of the page that was landed on. */
  path?: string | null;
}

/** The subset of `Storage` we need, so tests can inject a fake and Node needs no DOM. */
export interface AttributionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  /** Optional: used to drop an expired record. `localStorage` has it. */
  removeItem?(key: string): void;
}

export interface FirstTouch {
  /** Epoch milliseconds of the first attributed visit. */
  at: number;
  attribution: Attribution;
}

const STORED_VERSION = 1;

const CAMPAIGN_KEY_SET: ReadonlySet<string> = new Set(CAMPAIGN_KEYS);
const ATTRIBUTION_KEY_SET: ReadonlySet<string> = new Set<string>([
  ...CAMPAIGN_KEYS,
  ...CONTEXT_KEYS,
]);

/**
 * A high surrogate not followed by a low one, or a low surrogate not preceded
 * by a high one. Such a string is not valid UTF-8 on the wire, and a strict
 * JSON decoder on the receiving end can reject the whole payload it rides in.
 */
const LONE_SURROGATE =
  /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/**
 * Trim, scrub lone surrogates, clamp to `ATTRIBUTION_MAX_LENGTH` code points.
 * Returns undefined for anything that is not a non-empty string.
 */
function cleanValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim().replace(LONE_SURROGATE, "\uFFFD");
  if (trimmed.length === 0) return undefined;
  // `length` counts UTF-16 units; a string within the limit in units is within
  // it in code points too, so the (allocating) code-point path is rare.
  if (trimmed.length <= ATTRIBUTION_MAX_LENGTH) return trimmed;
  const codePoints = Array.from(trimmed);
  return codePoints.length > ATTRIBUTION_MAX_LENGTH
    ? codePoints.slice(0, ATTRIBUTION_MAX_LENGTH).join("")
    : trimmed;
}

/**
 * Read the campaign parameters out of a query string. Keys are matched
 * case-insensitively (`UTM_Source` counts), the first occurrence of a
 * duplicated key wins, and every unknown parameter is ignored. Never throws.
 */
export function parseAttribution(input: ParseAttributionInput): Attribution {
  const out: Attribution = {};
  try {
    const search = typeof input?.search === "string" ? input.search : "";
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search,
    );
    for (const [rawKey, rawValue] of params) {
      const key = rawKey.trim().toLowerCase();
      if (!CAMPAIGN_KEY_SET.has(key)) continue;
      const campaignKey = key as CampaignKey;
      if (out[campaignKey] !== undefined) continue;
      const value = cleanValue(rawValue);
      if (value !== undefined) out[campaignKey] = value;
    }
  } catch {
    // A query string URLSearchParams cannot parse is simply unattributed.
  }
  const referrer = cleanValue(input?.referrer);
  if (referrer !== undefined) out.referrer = referrer;
  const path = cleanValue(input?.path);
  if (path !== undefined) out.landing_path = path;
  return out;
}

/** True when the visit carries at least one `utm_*` parameter or click id. */
export function hasCampaignSignal(attribution: Attribution | null | undefined): boolean {
  if (!attribution) return false;
  return CAMPAIGN_KEYS.some((key) => attribution[key] !== undefined);
}

/**
 * Re-validate an attribution object that came from storage (or anywhere else
 * untrusted): keep only known keys with clean string values.
 */
function sanitizeAttribution(value: unknown): Attribution {
  const out: Attribution = {};
  if (!value || typeof value !== "object") return out;
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!ATTRIBUTION_KEY_SET.has(key)) continue;
    const clean = cleanValue(raw);
    if (clean !== undefined) out[key as AttributionKey] = clean;
  }
  return out;
}

/** Best-effort delete; storages without `removeItem` (or that throw) are left alone. */
function forgetStored(storage: AttributionStorage): void {
  try {
    storage.removeItem?.(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Nothing to do: an unreadable record is already treated as absent.
  }
}

/**
 * Parse and validate the stored first touch. Returns null when nothing is
 * stored, the JSON is corrupt, the shape is wrong, the record carries no
 * campaign signal (which a valid record never does), or the record is older
 * than `ATTRIBUTION_MAX_AGE_MS` relative to `now` (an expired record is also
 * removed from the storage when it can be). Never throws.
 */
export function readFirstTouch(
  storage: AttributionStorage,
  now: number = Date.now(),
): FirstTouch | null {
  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (typeof raw !== "string" || raw.length === 0) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as { v?: unknown; at?: unknown; attribution?: unknown };
    if (record.v !== STORED_VERSION) return null;
    if (typeof record.at !== "number" || !Number.isFinite(record.at)) return null;
    if (now - record.at > ATTRIBUTION_MAX_AGE_MS) {
      forgetStored(storage);
      return null;
    }
    const attribution = sanitizeAttribution(record.attribution);
    if (!hasCampaignSignal(attribution)) return null;
    return { at: record.at, attribution };
  } catch {
    return null;
  }
}

/**
 * Persist the first attributed visit. Writes ONLY when no valid record is
 * stored yet (first touch wins for `ATTRIBUTION_MAX_AGE_MS`) and ONLY when the
 * attribution carries a campaign signal — an organic visit must not claim the
 * first-touch slot. A corrupt or expired stored record counts as "nothing
 * stored" and is replaced.
 *
 * Returns true when a record was written. Storage failures are swallowed.
 */
export function rememberFirstTouch(
  storage: AttributionStorage,
  attribution: Attribution,
  now: number = Date.now(),
): boolean {
  if (!hasCampaignSignal(attribution)) return false;
  if (readFirstTouch(storage, now) !== null) return false;
  try {
    const record: { v: number; at: number; attribution: Attribution } = {
      v: STORED_VERSION,
      at: now,
      attribution: sanitizeAttribution(attribution),
    };
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Current-visit holder (module memory; one per page load)
// ---------------------------------------------------------------------------

let currentVisit: Attribution | null = null;

/**
 * Snapshot the current page load's attribution into memory. Called once at
 * boot, before consent exists; touches no storage.
 */
export function captureCurrentVisit(input: ParseAttributionInput): Attribution {
  currentVisit = parseAttribution(input);
  return currentVisit;
}

/** The boot snapshot, or null if `captureCurrentVisit` never ran. */
export function getCurrentVisit(): Attribution | null {
  return currentVisit;
}

/** Test seam. */
export function resetCurrentVisit(): void {
  currentVisit = null;
}

// ---------------------------------------------------------------------------
// PostHog property shapes (pure, so the mapping is tested here, not in the DOM)
// ---------------------------------------------------------------------------

/**
 * First-touch set under `initial_*` keys: `initial_utm_source`,
 * `initial_gclid`, `initial_referrer`, `initial_landing_path`, ...
 * Used both as set-once super properties and as set-once person properties.
 */
export function initialProperties(
  attribution: Attribution,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(sanitizeAttribution(attribution))) {
    out[`initial_${key}`] = value;
  }
  return out;
}

/** Current-visit campaign set under plain keys (`utm_source`, `gclid`, ...); no referrer/path. */
export function campaignProperties(
  attribution: Attribution,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = attribution[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * The current visit's campaign set as PostHog's own campaign handling shapes
 * it: EVERY campaign key, with `null` for the ones this visit did not carry.
 * Registering the full set (session-scoped, see `web/index.ts`) is what stops
 * a previous visit's `utm_campaign` from surviving next to this visit's
 * `gclid` — a partial register would merge into a contradictory union.
 */
export function campaignSessionProperties(
  attribution: Attribution,
): Record<CampaignKey, string | null> {
  const out = {} as Record<CampaignKey, string | null>;
  for (const key of CAMPAIGN_KEYS) out[key] = attribution[key] ?? null;
  return out;
}
